import { Grid2 } from '@mui/material';
import dayjs from 'dayjs';
import React from 'react';
import { defineMessages, useIntl } from 'react-intl';

import { EffectiveDateTimeInput } from '@components/DateInput';
import Dialog from '@components/Dialog/Dialog';
import RoundedButton from '@components/RoundedButton';

const messages = defineMessages({
  apply: {
    id: 'GLOBAL.save',
    defaultMessage: 'Save',
  },
  cancel: {
    id: 'GLOBAL.cancel',
    defaultMessage: 'Cancel',
  },
  dateFrom: {
    id: 'GLOBAL.dateFrom',
    defaultMessage: 'Start Date',
  },
  dateTo: {
    id: 'GLOBAL.dateTo',
    defaultMessage: 'End Date',
  },
  timeFrom: {
    id: 'GLOBAL.timeFrom',
    defaultMessage: 'Start Time',
  },
  timeTo: {
    id: 'GLOBAL.timeTo',
    defaultMessage: 'End Time',
  },
});

interface CustomRangeDialogProps {
  open: boolean;
  onCancel: () => void;
  onApply: (start: Date, end: Date) => void;
  initialTimeStart?: string;
  initialTimeEnd?: string;
}

export default function CustomRangeDialog({
  onCancel,
  onApply,
  open,
  initialTimeStart = '00:00',
  initialTimeEnd = '23:59',
}: CustomRangeDialogProps) {
  const { formatMessage } = useIntl();

  const [startDate, setStartDate] = React.useState<string>('');

  const [endDate, setEndDate] = React.useState<string>('');

  const [startTime, setStartTime] = React.useState<string>(
    initialTimeStart || ''
  );
  const [endTime, setEndTime] = React.useState<string>(initialTimeEnd || '');

  const mergeDateTime = (dateStr: string, timeStr: string) =>
    dateStr && timeStr ? dayjs(`${dateStr}T${timeStr}`).toDate() : null;

  const handleSubmit = () => {
    const start = mergeDateTime(startDate, startTime);
    const end = mergeDateTime(endDate, endTime);

    if (!start || !end) return;

    onApply(dayjs(start).utc().toDate(), dayjs(end).utc().toDate());
  };

  return (
    <Dialog
      open={open}
      onClose={onCancel}
      actions={
        <>
          <RoundedButton
            autoSize
            height={20}
            onClick={onCancel}
            variant="white"
          >
            {formatMessage(messages.cancel)}
          </RoundedButton>
          <RoundedButton
            autoSize
            height={20}
            onClick={handleSubmit}
            type="submit"
            variant="blue"
          >
            {formatMessage(messages.apply)}
          </RoundedButton>
        </>
      }
      content={
        <Grid2 container spacing={1}>
          <Grid2>
            <EffectiveDateTimeInput
              label={formatMessage(messages.dateFrom)}
              name="startDate"
              type="date"
              max={endDate ? endDate : dayjs().format('YYYY-MM-DD')}
              value={startDate}
              onChange={(val) => setStartDate(val || '')}
            />
          </Grid2>
          <Grid2>
            <EffectiveDateTimeInput
              label={formatMessage(messages.timeFrom)}
              name="startTime"
              type="time"
              value={startTime}
              onChange={(val) => setStartTime(val || '')}
            />
          </Grid2>
          <Grid2>
            <EffectiveDateTimeInput
              label={formatMessage(messages.dateTo)}
              name="endDate"
              type="date"
              max={dayjs().format('YYYY-MM-DD')}
              value={endDate}
              onChange={(val) => setEndDate(val || '')}
              min={startDate || undefined}
            />
          </Grid2>
          <Grid2>
            <EffectiveDateTimeInput
              label={formatMessage(messages.timeTo)}
              name="endTime"
              type="time"
              value={endTime}
              onChange={(val) => setEndTime(val || '')}
            />
          </Grid2>
        </Grid2>
      }
    />
  );
}
